import React, { useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import InlineAccountTokenInfo from "./InlineAccountTokenInfo";
import DepositInteractionModal from "../InteractionModal/DepositInteractionModal";
import LeverageInteractionModal from "../InteractionModal/LeverageInteractionModal";
import WithdrawInteractionModal from "../InteractionModal/WithdrawInteractionModal";
import DeleverageInteractionModal from "../InteractionModal/DeleverageInteractionModal";
import DisabledButtonHelper from "../DisabledButtonHelper";
import PairLeftTitle from "../PairLeftTitle";
import { useOnchainAccountCollateral } from "../../hooks/useOnchain";
import useGenericCall from "../../hooks/useGenericCall";
import { DISABLED_BORROWS } from "../../utils/constants";
import { useNetworkName } from "../../hooks/useNetwork";

const EXCHANGE_RATE_CAP = 10;

export default function AccountLendingPoolLPRow() {
  const data = useGenericCall(useOnchainAccountCollateral(), async onchainAccountCollateral => ({
    symbol: await (await onchainAccountCollateral.getPoolToken().getOffchainPoolToken()).getSymbol(),
    deposited: await onchainAccountCollateral.getDeposited(),
    depositedUSD: await onchainAccountCollateral.getDepositedUSD(),
    maxDeleverage: await onchainAccountCollateral.getLendingPool().getMaxDeleverage(1), //0% slippage
    pairAddress: await (await onchainAccountCollateral.getPoolToken().getOffchainPoolToken()).getUnderlyingAddress(),
    exchangeRate: await onchainAccountCollateral.getPoolToken().getLendingPool().getStakedLPExchangeRate()
  }));

  const network = useNetworkName();

  const [showDepositModal, toggleDepositModal] = useState(false);
  const [showWithdrawModal, toggleWithdrawModal] = useState(false);
  const [showLeverageModal, toggleLeverageModal] = useState(false);
  const [showDeleverageModal, toggleDeleverageModal] = useState(false);

  if (!data) return (null);

  // In case borrowing is intentionally disabled for risky pairs
  const borrowLimitReached = DISABLED_BORROWS[network]?.includes(data?.pairAddress);

  const leverageDisabledInfo = borrowLimitReached ?
    `Borrow limit reached. Borrowing is currently disabled for this pair.` 
    : `You need to deposit the ${data.symbol} LP first in order to leverage it.`;
  const withdrawDisabledInfo = `You haven't deposited any ${data.symbol} yet.`;
  const deleverageDisabledInfo = `You need to open a leveraged position in order to deleverage it.`;

  // In case exhcnage rate is too high.
  // ie. see FBOMB/USDC pair on base: lending-pool/6/0x753ea0ade9cbff68cbaffaedf7863b8225c93a64
  const exchangeRateTooHigh = data.exchangeRate > EXCHANGE_RATE_CAP;

  return (<>
    <Row className="account-lending-pool-row">
      <Col md={3} className="currency-name">
        <PairLeftTitle/>
      </Col>
      <Col md={4} className="inline-account-token-info-container">
        <InlineAccountTokenInfo
          name="Deposited"
          symbol="LP"
          value={data.deposited}
          valueUSD={data.depositedUSD}
        />
      </Col>
      <Col md={5} className="btn-table">
        <Row>
          <Col>
            {exchangeRateTooHigh ? (
              <DisabledButtonHelper text={`Deposits are currently disabled, exchange rate too high (${data.exchangeRate})`}>
                {"Deposit"}
              </DisabledButtonHelper>
            ) : (
              <Button variant="primary" onClick={() => toggleDepositModal(true)}>
                Deposit
              </Button>
            )}
          </Col>
          <Col>
            { data.deposited > 0 ? (
              <Button variant="primary" onClick={() => toggleWithdrawModal(true)}>Withdraw</Button>
            ) : (
              <DisabledButtonHelper text={withdrawDisabledInfo}>{"Withdraw"}</DisabledButtonHelper>
            ) }
          </Col>
        </Row>
        <Row>
          <Col>
            { data.deposited > 0 && !borrowLimitReached? (
              <Button variant="primary" onClick={() => toggleLeverageModal(true)}>Leverage</Button>
            ) : (
              <DisabledButtonHelper text={leverageDisabledInfo}>{"Leverage"}</DisabledButtonHelper>
            ) }
          </Col>
          <Col>
            { data.maxDeleverage > 0 ? (
              <Button variant="primary" onClick={() => toggleDeleverageModal(true)}>Deleverage</Button>
            ) : (
              <DisabledButtonHelper text={deleverageDisabledInfo}>{"Deleverage"}</DisabledButtonHelper>
            ) }
          </Col>
        </Row>
      </Col>
    </Row>
    <DepositInteractionModal 
      show={showDepositModal} 
      toggleShow={toggleDepositModal}
    />
    <WithdrawInteractionModal 
      show={showWithdrawModal} 
      toggleShow={toggleWithdrawModal}
    />
    <LeverageInteractionModal 
      show={showLeverageModal} 
      toggleShow={toggleLeverageModal}
    />
    <DeleverageInteractionModal 
      show={showDeleverageModal} 
      toggleShow={toggleDeleverageModal}
    />
  </>);
}
